
export const formatLocalDateKey = (date) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return null;
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

export const parseLocalDate = (value) => {
  if (!value && value !== 0) return null;
  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return null;
    return new Date(value.getFullYear(), value.getMonth(), value.getDate());
  }

  if (typeof value === 'string') {
    const match = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (match) {
      const [, year, month, day] = match.map(Number);
      const date = new Date(year, month - 1, day);
      if (
        date.getFullYear() === year &&
        date.getMonth() === month - 1 &&
        date.getDate() === day
      ) {
        return date;
      }
      return null;
    }
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;
  return new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate());
};

const getSafeBalance = (account) => {
  const balance = Number.parseFloat(account?.balance);
  return Number.isFinite(balance) ? balance : 0;
};

const getTransactionAmount = (transaction) => {
  const amount = Number.parseFloat(transaction?.amount);
  // Direction is represented by fromAccount/toAccount. Historic transactions
  // imported from bank statements may still carry a signed amount.
  return Number.isFinite(amount) ? Math.abs(amount) : null;
};

const addCalendarYears = (date, years) => {
  const source = parseLocalDate(date);
  if (!source) return null;
  const targetYear = source.getFullYear() + years;
  const lastDay = new Date(targetYear, source.getMonth() + 1, 0).getDate();
  return new Date(targetYear, source.getMonth(), Math.min(source.getDate(), lastDay));
};

export const resolveAccountAvailabilityDate = (account, birthDate) => {
  if (account?.type !== 'Asset') return null;

  if (account.availabilityType === 'age') {
    const age = Number.parseInt(account.availableFromAge, 10);
    if (!Number.isFinite(age) || age < 0) return null;
    return addCalendarYears(birthDate, age);
  }

  if (account.availabilityType === 'date') {
    return parseLocalDate(account.availableFromDate);
  }

  return null;
};

export const buildCashflowProjection = ({
  accounts = [],
  transactions = [],
  startDate = new Date(),
  endDate = null,
  birthDate = null,
  respectAccountAvailability = false,
} = {}) => {
  const projectionStart = parseLocalDate(startDate) || parseLocalDate(new Date());
  const projectionEnd = endDate ? parseLocalDate(endDate) : null;
  const balances = {};
  const accountPoints = {};
  const accountAccessible = {};
  const dailyMovements = new Map();

  const getDailyMovement = (date) => {
    const dateKey = formatLocalDateKey(date);
    if (!dailyMovements.has(dateKey)) {
      dailyMovements.set(dateKey, { date, accountChanges: {}, releasedAccountIds: [] });
    }
    return dailyMovements.get(dateKey);
  };

  accounts.forEach(([id, account]) => {
    balances[id] = getSafeBalance(account);
    accountPoints[id] = [];

    const availabilityDate = respectAccountAvailability
      ? resolveAccountAvailabilityDate(account, birthDate)
      : null;
    const isLiability = respectAccountAvailability && account?.type === 'Loan';
    const isAccessible =
      !isLiability && (!availabilityDate || availabilityDate <= projectionStart);
    accountAccessible[id] = isAccessible;

    if (
      !isAccessible &&
      availabilityDate &&
      (!projectionEnd || availabilityDate <= projectionEnd)
    ) {
      getDailyMovement(availabilityDate).releasedAccountIds.push(id);
    }
  });

  const initialBalance = Object.keys(balances).reduce(
    (sum, id) => sum + (accountAccessible[id] ? balances[id] : 0),
    0
  );
  const selectedAccountIds = new Set(Object.keys(balances));

  transactions.forEach((transaction) => {
    // Paid transactions have already changed the current account balances.
    if (transaction?.isPaid) return;

    const date = parseLocalDate(transaction?.date);
    if (!date || (projectionEnd && date > projectionEnd)) return;

    const amount = getTransactionAmount(transaction);
    if (amount === null) return;

    const fromIncluded = selectedAccountIds.has(transaction?.fromAccount);
    const toIncluded = selectedAccountIds.has(transaction?.toAccount);
    if (!fromIncluded && !toIncluded) return;

    // Overdue unpaid transactions are not reflected in the current balances yet.
    // Apply them immediately at the start of the projection instead of omitting them.
    const effectiveDate = date < projectionStart ? projectionStart : date;
    const movement = getDailyMovement(effectiveDate);

    if (fromIncluded) {
      movement.accountChanges[transaction.fromAccount] =
        (movement.accountChanges[transaction.fromAccount] || 0) - amount;
    }
    if (toIncluded) {
      movement.accountChanges[transaction.toAccount] =
        (movement.accountChanges[transaction.toAccount] || 0) + amount;
    }
  });

  const startKey = formatLocalDateKey(projectionStart);
  const labels = [startKey];
  const totalPoints = [{ date: startKey, balance: initialBalance }];
  Object.keys(balances).forEach((id) => {
    accountPoints[id].push({ date: startKey, balance: balances[id] });
  });

  let runningTotal = initialBalance;
  let runOutDate = initialBalance <= 0 ? projectionStart : null;
  let runOutBalance = initialBalance <= 0 ? initialBalance : null;
  let lastImpactDate = null;

  [...dailyMovements.entries()]
    .sort(([dateA], [dateB]) => dateA.localeCompare(dateB))
    .forEach(([dateKey, movement]) => {
      if (dateKey !== startKey) labels.push(dateKey);
      lastImpactDate = movement.date;

      Object.entries(movement.accountChanges).forEach(([accountId, change]) => {
        balances[accountId] += change;
        if (accountAccessible[accountId]) runningTotal += change;
        accountPoints[accountId].push({ date: dateKey, balance: balances[accountId] });
      });

      movement.releasedAccountIds.forEach((accountId) => {
        if (accountAccessible[accountId]) return;
        accountAccessible[accountId] = true;
        runningTotal += balances[accountId];
      });

      totalPoints.push({ date: dateKey, balance: runningTotal });
      if (!runOutDate && runningTotal <= 0) {
        runOutDate = movement.date;
        runOutBalance = runningTotal;
      }
    });

  return {
    initialBalance,
    finalBalance: runningTotal,
    runOutDate,
    runOutBalance,
    lastImpactDate,
    labels,
    accountPoints,
    totalPoints,
  };
};

export const calculateAgeOnDate = (birthDate, targetDate) => {
  const birth = parseLocalDate(birthDate);
  const target = parseLocalDate(targetDate);
  if (!birth || !target) return null;

  let age = target.getFullYear() - birth.getFullYear();
  const hasHadBirthday =
    target.getMonth() > birth.getMonth() ||
    (target.getMonth() === birth.getMonth() && target.getDate() >= birth.getDate());
  if (!hasHadBirthday) age -= 1;
  return age;
};

export const addCalendarMonths = (date, months) => {
  const source = parseLocalDate(date);
  if (!source) return null;
  const targetMonth = source.getMonth() + months;
  const result = new Date(source.getFullYear(), targetMonth, 1);
  const lastDay = new Date(result.getFullYear(), result.getMonth() + 1, 0).getDate();
  result.setDate(Math.min(source.getDate(), lastDay));
  return result;
};


export const formatTimeUntilDate = (fromDate, toDate) => {
  const from = parseLocalDate(fromDate);
  const to = parseLocalDate(toDate);
  if (!from || !to) return null;
  if (to <= from) return '0 months';

  let totalMonths =
    (to.getFullYear() - from.getFullYear()) * 12 +
    (to.getMonth() - from.getMonth());
  let anchor = addCalendarMonths(from, totalMonths);

  if (anchor > to) {
    totalMonths -= 1;
    anchor = addCalendarMonths(from, totalMonths);
  }

  const nextAnchor = addCalendarMonths(from, totalMonths + 1);
  const elapsed = to.getTime() - anchor.getTime();
  const monthLength = nextAnchor.getTime() - anchor.getTime();
  if (elapsed >= monthLength / 2) totalMonths += 1;

  const years = Math.floor(totalMonths / 12);
  const months = totalMonths % 12;
  const parts = [];
  if (years > 0) parts.push(years + ' ' + (years === 1 ? 'year' : 'years'));
  if (months > 0 || years === 0) {
    parts.push(months + ' ' + (months === 1 ? 'month' : 'months'));
  }
  return parts.join(', ');
};
